import React from 'react';
import moment from 'moment';
import { faClock, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input } from 'reactstrap';
import Select from 'react-select';
import { v4 as uuid } from 'uuid';
import StaggeredStartTimeModal from './StaggeredStartTimeModal';
import ConstrainedTimeInput from '../common/forms/ConstrainedTimeInput';

/**
 * Manages a list of override start time entries for a resource type.
 *
 * Uses a children-as-render-prop pattern: `children(addButton)` is called so
 * the caller can position the add button wherever it belongs in its own layout
 * (e.g. inside a reactstrap InputGroup). Time entry rows are rendered directly
 * below whatever `children` produces.
 *
 * Props:
 *   max      {number}   Resource count — caps how many entries can be added.
 *   children {function} Render prop receiving the add <Button> element.
 */
export default class JobResourceOverrideList extends React.Component {
    constructor(props) {
        super(props);
        this.modalRef = React.createRef();

        const { jobResourceOverrides } = props;
        this.state = {
            jobResourceOverrides: (jobResourceOverrides || []).map(
                (jobResourceOverride) => ({
                    id: jobResourceOverride.id ?? uuid(),
                    value:
                        jobResourceOverride.value ??
                        (jobResourceOverride.startTime
                            ? moment(jobResourceOverride.startTime).format(
                                  'HH:mm'
                              )
                            : ''),
                    startLocationId:
                        jobResourceOverride.startLocationId ??
                        jobResourceOverride.arcGisOriginId ??
                        null,
                    startLocationText:
                        jobResourceOverride.startLocationText ??
                        jobResourceOverride.roadName ??
                        '',
                    endLocationId:
                        jobResourceOverride.endLocationId ??
                        jobResourceOverride.arcGisDestinationId ??
                        null,
                    endLocationText: jobResourceOverride.endLocationText ?? '',
                })
            ),
        };
    }

    syncOverridesChange = () => {
        const { onOverridesChange } = this.props;

        if (onOverridesChange) {
            onOverridesChange(this.state.jobResourceOverrides);
        }
    };

    componentDidUpdate(prevProps) {
        const { max } = this.props;
        if (prevProps.max !== max) {
            const count = parseInt(max, 10) || 0;
            this.setState(
                ({ jobResourceOverrides }) => ({
                    jobResourceOverrides: jobResourceOverrides.slice(
                        0,
                        Math.max(0, count)
                    ),
                }),
                this.syncOverridesChange
            );
        }
    }

    addTime = () => {
        if (this.modalRef.current) {
            this.modalRef.current.open();
        }
    };

    handleModalSave = (newEntries) => {
        this.setState(
            ({ jobResourceOverrides }) => ({
                jobResourceOverrides: [...jobResourceOverrides, ...newEntries],
            }),
            this.syncOverridesChange
        );
    };

    removeTime = (id) => {
        this.setState(
            ({ jobResourceOverrides }) => ({
                jobResourceOverrides: jobResourceOverrides.filter(
                    (t) => t.id !== id
                ),
            }),
            this.syncOverridesChange
        );
    };

    updateTime = (id, value) => {
        this.setState(
            ({ jobResourceOverrides }) => ({
                jobResourceOverrides: jobResourceOverrides.map((t) =>
                    t.id === id ? { ...t, value } : t
                ),
            }),
            this.syncOverridesChange
        );
    };

    updateStartLocation = (id, locationId, locationText) => {
        this.setState(
            ({ jobResourceOverrides }) => ({
                jobResourceOverrides: jobResourceOverrides.map((t) =>
                    t.id === id
                        ? {
                              ...t,
                              startLocationId: locationId,
                              startLocationText: locationText,
                          }
                        : t
                ),
            }),
            this.syncOverridesChange
        );
    };

    updateEndLocation = (id, locationId, locationText) => {
        this.setState(
            ({ jobResourceOverrides }) => ({
                jobResourceOverrides: jobResourceOverrides.map((t) =>
                    t.id === id
                        ? {
                              ...t,
                              endLocationId: locationId,
                              endLocationText: locationText,
                          }
                        : t
                ),
            }),
            this.syncOverridesChange
        );
    };

    render() {
        const {
            max,
            children,
            arcGisLocations,
            jobStartTime,
            disabled,
            resourceLabel,
            workDateLabel,
        } = this.props;
        const { jobResourceOverrides } = this.state;
        const count = parseInt(max, 10) || 0;
        const availableOverrideSlots = Math.max(
            0,
            count - jobResourceOverrides.length
        );
        const canAdd = availableOverrideSlots > 0;
        const hasLocations = arcGisLocations && arcGisLocations.length > 0;

        const addButton = (
            <Button
                color={canAdd ? 'success' : 'secondary'}
                outline={canAdd}
                size="sm"
                disabled={!canAdd || disabled}
                title="Add override start time"
                onClick={this.addTime}
            >
                <FontAwesomeIcon icon={faClock} />
                <FontAwesomeIcon icon={faPlus} className="ms-1" size="xs" />
            </Button>
        );

        return (
            <>
                {children(addButton)}

                {jobResourceOverrides.map((t) => {
                    const isValid = moment(t.value, 'HH:mm', true).isValid();
                    const showInvalid = t.value !== '' && !isValid;

                    return (
                        <div
                            key={t.id}
                            className="d-flex flex-column mt-1 gap-1 job-resource-override-list"
                        >
                            <div className="d-flex align-items-center gap-1">
                                <div className="w-75 mb-1">
                                    <ConstrainedTimeInput
                                        bsSize="sm"
                                        value={t.value}
                                        onChange={(e) =>
                                            this.updateTime(
                                                t.id,
                                                e.target.value
                                            )
                                        }
                                        invalid={showInvalid}
                                        valid={isValid}
                                    />
                                </div>
                                <Button
                                    color="danger"
                                    outline
                                    size="sm"
                                    onClick={() => this.removeTime(t.id)}
                                    title="Remove"
                                    className="ml-5"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            </div>
                            <div className="d-flex gap-1">
                                {hasLocations ? (
                                    <>
                                        <Select
                                            className="job-form-select-min flex-grow-1"
                                            classNamePrefix="react-select"
                                            placeholder="Start location…"
                                            isClearable
                                            options={arcGisLocations}
                                            getOptionValue={(agl) => agl.id}
                                            getOptionLabel={(agl) =>
                                                agl.addrLoc
                                            }
                                            value={
                                                arcGisLocations.find(
                                                    (agl) =>
                                                        agl.id ===
                                                        t.startLocationId
                                                ) || null
                                            }
                                            onChange={(sel) =>
                                                this.updateStartLocation(
                                                    t.id,
                                                    sel ? sel.id : null,
                                                    sel ? sel.addrLoc : ''
                                                )
                                            }
                                        />
                                        <Select
                                            className="job-form-select-min flex-grow-1"
                                            classNamePrefix="react-select"
                                            placeholder="End location…"
                                            isClearable
                                            options={arcGisLocations}
                                            getOptionValue={(agl) => agl.id}
                                            getOptionLabel={(agl) =>
                                                agl.addrLoc
                                            }
                                            value={
                                                arcGisLocations.find(
                                                    (agl) =>
                                                        agl.id ===
                                                        t.endLocationId
                                                ) || null
                                            }
                                            onChange={(sel) =>
                                                this.updateEndLocation(
                                                    t.id,
                                                    sel ? sel.id : null,
                                                    sel ? sel.addrLoc : ''
                                                )
                                            }
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Input
                                            bsSize="sm"
                                            placeholder="Start location…"
                                            value={t.startLocationText}
                                            onChange={(e) =>
                                                this.updateStartLocation(
                                                    t.id,
                                                    null,
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <Input
                                            bsSize="sm"
                                            placeholder="End location…"
                                            value={t.endLocationText}
                                            onChange={(e) =>
                                                this.updateEndLocation(
                                                    t.id,
                                                    null,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
                <StaggeredStartTimeModal
                    ref={this.modalRef}
                    maxCount={availableOverrideSlots}
                    jobStartTime={jobStartTime}
                    arcGisLocations={arcGisLocations}
                    onSave={this.handleModalSave}
                    disabled={disabled}
                    resourceLabel={resourceLabel}
                    workDateLabel={workDateLabel}
                />
            </>
        );
    }
}
